/**
 * <LiveSessionBar> — l'en-tête d'un live, le seul de l'écran (molecule).
 *
 * Deux formes, une par taille d'écran (Wajih, 17/10 : « un seul en-tête ») :
 *  - **grand écran** : la séance — la capsule du tuteur ; une ligne « 9h20 avec Gabriel » (l'heure en
 *    gras, `liveHeadingRest`) ; la ligne de la séance (`<SessionDetailsLine>` : ses matières, sa durée,
 *    sans le tuteur déjà nommé) —, puis les autres élèves connectés, et la liste des activités. Les
 *    élèves connectés remplacent leurs visages sur la visio (Wajih, 20/10) : « Deux autres élèves
 *    connectés », le nombre en toutes lettres, sans me compter, derrière la pastille verte du direct
 *    (`<LiveDot>`, Wajih, daily du 23/10) ; rien quand je suis seul (`connectedStudentsLabel`) ;
 *  - **téléphone** (`viewSwitch`) : la bascule « Activité » / « Visio » (`<LiveViewSwitch>`), puis
 *    la liste des activités. C'est tout : ni la séance, l'essentiel doit tenir dans un écran de
 *    téléphone épuré.
 *
 * Pas de chip du mode, nulle part (Wajih, 19/10 : au grand écran, elle n'apportait rien et était mal
 * placée) : c'est la bulle tandem (`<LiveTandemBubble>`) qui dit le mode, Sam devant en Concentré, le
 * masteur devant en Dialogue.
 *
 * La liste des activités (`activities`) est l'icône de liste et son compte : elle ouvre « Mes
 * activités », la todo du live. Pas de minuteur de séance : il n'existe pas aujourd'hui.
 *
 * Pas d'heure courante non plus (Wajih, 20/10 : posée seule à droite de la bascule, elle « fait
 * cheap ») : l'ancien live l'écrivait dans son pied ; l'heure du téléphone est déjà en haut de l'écran.
 *
 * Pas de sortie ici : on raccroche par le bouton rouge de la barre du bas (Wajih, 19/10, contre la
 * croix proposée après le test du 18/10).
 *
 * La barre prend la surface d'une carte et un filet dessous, comme l'en-tête d'une conversation.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveSessionHeading } from "../../../ports/primary/ui/types/studentLive"
import { formatHour } from "../../../ports/primary/ui/formatters/dateFormatter"
import {
    connectedStudentsLabel,
    liveHeadingRest,
    tutorShortName,
} from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { Avatar } from "../../atoms/Avatar"
import { LiveDot } from "../../atoms/LiveDot"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { BadgedIconButton } from "../BadgedIconButton"
import { LiveViewSwitch, type LiveViewSwitchProps } from "../LiveViewSwitch"
import { SessionDetailsLine } from "../SessionDetailsLine"
import { AVATAR_SIZE, BOW_ROOM } from "../SessionRow/SessionRow"

// Ce que l'en-tête lit d'une séance : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UILiveSessionHeading }

/** La liste des activités du live : leur nombre, et ce qui l'ouvre. */
export type UILiveActivitiesAccess = {
    count: number
    onPress: () => void
}

/** La bascule du téléphone, telle que l'en-tête la reçoit. */
export type LiveSessionBarViewSwitch = Omit<LiveViewSwitchProps, "testID">

export type LiveSessionBarProps = {
    session: UILiveSessionHeading
    /** La liste des activités. Absente : pas de bouton. */
    activities?: UILiveActivitiesAccess
    /**
     * Le téléphone : la bascule à la place de la séance. Absente (défaut) : la forme
     * du grand écran.
     */
    viewSwitch?: LiveSessionBarViewSwitch
    /**
     * Les élèves connectés au live, moi compris : les autres sont dits au grand écran, à partir d'un.
     * Absent : rien.
     */
    connectedStudents?: number
    testID?: string
}

/** La cible d'un doigt. */
const ACTIVITIES_SIZE = 44

const TEXTS = {
    activities: (count: number) => (count === 1 ? "Mes activités, 1 activité" : `Mes activités, ${count} activités`),
    connected: (label: string) => `${label}, en direct`,
}

export function LiveSessionBar(props: LiveSessionBarProps) {
    const { session, activities, viewSwitch, connectedStudents, testID } = props
    const rest = liveHeadingRest(session.tutor.firstname)
    const connected = connectedStudentsLabel(connectedStudents)

    const activitiesButton = activities != null && (
        <BadgedIconButton
            iconName="tasks"
            appearance="transparent"
            iconColor="primary"
            size={ACTIVITIES_SIZE}
            iconSize={22}
            count={activities.count}
            countVariant="neutral"
            countLabel={null}
            onPress={activities.onPress}
            accessibilityLabel={TEXTS.activities(activities.count)}
        />
    )

    if (viewSwitch != null) {
        return (
            <View style={styles.bar(true)} testID={testID}>
                <Row gap="2" align="center">
                    <View style={styles.body}>
                        <LiveViewSwitch {...viewSwitch} />
                    </View>
                    {activitiesButton}
                </Row>
            </View>
        )
    }

    return (
        <View style={styles.bar(false)} testID={testID}>
            <Row gap="3" align="center">
                <View style={styles.avatar}>
                    <Avatar
                        uri={session.tutor.avatarUrl}
                        name={tutorShortName(session.tutor)}
                        size={AVATAR_SIZE}
                        signed
                    />
                </View>
                <Stack gap="0" style={styles.body}>
                    <Text size="text-md" weight="bold" color="text-strong" numberOfLines={1}>
                        {formatHour(session.date)}
                        {rest !== "" && (
                            <Text size="text-md" weight="regular" color="text-strong">
                                {` ${rest}`}
                            </Text>
                        )}
                    </Text>
                    <SessionDetailsLine session={session} withTutor={rest === ""} />
                </Stack>
                {connected !== "" && (
                    <View
                        style={styles.connected}
                        accessible
                        accessibilityRole="text"
                        accessibilityLabel={TEXTS.connected(connected)}
                    >
                        <Row gap="2" align="center">
                            <LiveDot />
                            <Text
                                size="text-sm"
                                weight="medium"
                                color="text-strong"
                                numberOfLines={1}
                                style={styles.connectedLabel}
                            >
                                {connected}
                            </Text>
                        </Row>
                    </View>
                )}
                {activitiesButton}
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    bar: (phone: boolean) => ({
        backgroundColor: theme.colors["surface-raised"],
        borderColor: theme.colors.border,
        borderBottomWidth: 1,
        paddingLeft: theme.spacing[phone ? "3" : "4"],
        // Le bouton de la liste porte déjà sa marge dans sa cible de 44 px.
        paddingRight: theme.spacing["2"],
        paddingVertical: theme.spacing["2"],
    }),
    /** Le nœud de la capsule pend sous elle. */
    avatar: { paddingBottom: BOW_ROOM },
    body: { flex: 1, flexShrink: 1 },
    /** Sur un en-tête étroit, les élèves connectés se coupent (points de suspension) plutôt que de déborder. */
    connected: { flexShrink: 1, minWidth: 0 },
    connectedLabel: { flexShrink: 1 },
}))
